import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useLocation } from "wouter";
import AdminLayout from "./AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Crown, User, RefreshCw, Mail, Loader2, MessageCircle, Phone, AtSign, CheckCircle2, Undo2, Search, AlertTriangle, UserX, History, LogIn } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

interface WhatsAppLogEntry {
  id: number;
  memberId: number;
  action: "registered" | "confirmed" | "removed";
  phone: string | null;
  username: string | null;
  reason: string | null;
  previousContact: string | null;
  createdAt: string;
  email: string;
  displayName: string | null;
}

interface MemberRow {
  id: number;
  clerkUserId: string;
  email: string;
  notificationEmail: string | null;
  displayName: string | null;
  membershipTier: string;
  stripeCustomerId: string | null;
  subscriptionStatus: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  whatsappPhone: string | null;
  whatsappUsername: string | null;
  whatsappRequestStatus: string | null;
  whatsappRequestedAt: string | null;
  whatsappConfirmedAt: string | null;
  whatsappPendingRemoval: string | null;
  createdAt: string;
}

function whatsappContactLabel(m: { whatsappPhone: string | null; whatsappUsername: string | null }): string | null {
  if (m.whatsappUsername) return `@${m.whatsappUsername}`;
  return m.whatsappPhone;
}

function formatExpiry(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("zh-HK", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatJoinedAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("zh-HK", {
    timeZone: "Asia/Hong_Kong",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminMembersPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [sendingDailyId, setSendingDailyId] = useState<number | null>(null);
  const [confirmMember, setConfirmMember] = useState<MemberRow | null>(null);

  async function doSendDailyEmail(m: MemberRow) {
    setSendingDailyId(m.id);
    try {
      const token = await getToken();
      const res = await fetch(`/api/admin/members/${m.id}/daily-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "發送失敗");
      toast({ title: "今日更新電郵已發送", description: `已寄至 ${data.to}，共 ${data.tenderCount} 項招標` });
    } catch (e: any) {
      toast({ title: "發送失敗", description: e.message, variant: "destructive" });
    } finally {
      setSendingDailyId(null);
    }
  }

  const { data: members, isLoading } = useQuery<MemberRow[]>({
    queryKey: ["admin-members"],
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/members", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
  });

  const setTier = useMutation({
    mutationFn: async ({ id, tier }: { id: number; tier: string }) => {
      const token = await getToken();
      return fetch(`/api/admin/members/${id}/tier`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier }),
      }).then(r => r.json());
    },
    onMutate: ({ id }) => setLoadingId(id),
    onSettled: () => {
      setLoadingId(null);
      qc.invalidateQueries({ queryKey: ["admin-members"] });
    },
  });

  const [whatsappLoadingId, setWhatsappLoadingId] = useState<number | null>(null);

  const confirmWhatsApp = useMutation({
    mutationFn: async (id: number) => {
      const token = await getToken();
      const res = await fetch(`/api/admin/members/${id}/whatsapp/confirm`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "確認失敗");
      return res.json();
    },
    onMutate: (id) => setWhatsappLoadingId(id),
    onSuccess: () => toast({ title: "已確認加入 WhatsApp 社群" }),
    onError: (e: any) => toast({ title: "確認失敗", description: e.message, variant: "destructive" }),
    onSettled: () => {
      setWhatsappLoadingId(null);
      qc.invalidateQueries({ queryKey: ["admin-members"] });
    },
  });

  const resetWhatsApp = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: "expired" | "manual" }) => {
      const token = await getToken();
      const res = await fetch(`/api/admin/members/${id}/whatsapp/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "重設失敗");
      return res.json();
    },
    onMutate: ({ id }) => setWhatsappLoadingId(id),
    onSuccess: () => toast({ title: "已重設登記狀態" }),
    onError: (e: any) => toast({ title: "重設失敗", description: e.message, variant: "destructive" }),
    onSettled: () => {
      setWhatsappLoadingId(null);
      qc.invalidateQueries({ queryKey: ["admin-members"] });
      qc.invalidateQueries({ queryKey: ["admin-whatsapp-logs"] });
    },
  });

  const [search, setSearch] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [logSearch, setLogSearch] = useState("");

  const { data: logsData, isLoading: logsLoading } = useQuery<{ logs: WhatsAppLogEntry[] }>({
    queryKey: ["admin-whatsapp-logs"],
    enabled: showLogs,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/whatsapp/logs", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
  });

  const pendingWhatsApp = (members ?? []).filter(m => m.whatsappRequestStatus === "pending");

  // 同一電話號碼或 @username 被多於一位會員登記（可能是重複申請或濫用同一帳號）
  const phoneGroups = new Map<string, MemberRow[]>();
  (members ?? []).forEach(m => {
    const contact = m.whatsappUsername ? `@${m.whatsappUsername.toLowerCase()}` : m.whatsappPhone;
    if (!contact) return;
    const key = contact.replace(/[\s-]/g, "");
    if (!phoneGroups.has(key)) phoneGroups.set(key, []);
    phoneGroups.get(key)!.push(m);
  });
  const duplicatePhoneGroups = Array.from(phoneGroups.entries()).filter(([, list]) => list.length > 1);
  const duplicatePhoneSet = new Set(duplicatePhoneGroups.map(([phone]) => phone));

  // 已確認加入 WhatsApp 社群，但會籍已非付費（到期/取消），需要從社群手動移除
  const expiredButConfirmed = (members ?? []).filter(
    m => m.whatsappRequestStatus === "confirmed" && m.membershipTier !== "paid"
  );

  const searchQuery = search.trim().toLowerCase();
  const filteredMembers = (members ?? []).filter(m => {
    if (!searchQuery) return true;
    return (
      m.email.toLowerCase().includes(searchQuery) ||
      (m.displayName ?? "").toLowerCase().includes(searchQuery) ||
      (m.whatsappPhone ?? "").toLowerCase().includes(searchQuery) ||
      (m.whatsappUsername ?? "").toLowerCase().includes(searchQuery)
    );
  });

  const logSearchQuery = logSearch.trim().toLowerCase();
  const filteredLogs = (logsData?.logs ?? []).filter(log => {
    if (!logSearchQuery) return true;
    return (
      log.email.toLowerCase().includes(logSearchQuery) ||
      (log.displayName ?? "").toLowerCase().includes(logSearchQuery) ||
      (log.phone ?? "").toLowerCase().includes(logSearchQuery) ||
      (log.username ?? "").toLowerCase().includes(logSearchQuery)
    );
  });

  return (
    <AdminLayout tab="members">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">會員管理</h2>
            <p className="text-sm text-muted-foreground">可一鍵切換會籍（用於測試收費流程）</p>
            <p className="text-sm font-medium text-orange-500">
              Total members: {isLoading ? "…" : (members ?? []).length}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setShowLogs(v => !v)}
            >
              <History className="h-3.5 w-3.5 mr-1.5" /> {showLogs ? "隱藏歷史紀錄" : "WhatsApp 歷史紀錄"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["admin-members"] })}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> 刷新
            </Button>
          </div>
        </div>

        {showLogs && (
          <div className="border rounded-lg overflow-hidden bg-card">
            <div className="px-4 py-2.5 border-b flex items-center gap-2">
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12.04 0C5.446 0 .06 5.386.06 11.984c0 2.112.554 4.169 1.606 5.986L0 24l6.185-1.62a11.9 11.9 0 005.855 1.494h.005c6.593 0 11.98-5.386 11.98-11.985 0-3.2-1.245-6.21-3.505-8.47A11.916 11.916 0 0012.04 0zm0 21.985a9.94 9.94 0 01-5.073-1.393l-.364-.216-3.67.962.982-3.584-.238-.368a9.916 9.916 0 01-1.52-5.402c0-5.494 4.47-9.964 9.97-9.964a9.9 9.9 0 017.043 2.92 9.9 9.9 0 012.917 7.045c0 5.494-4.47 9.964-9.965 9.964z" />
              </svg>
              <h3 className="text-sm font-semibold">WhatsApp 社群登記／確認／移除紀錄</h3>
            </div>
            <div className="px-4 py-2 border-b relative">
              <Search className="h-3.5 w-3.5 absolute left-6.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                placeholder="搜尋 email、姓名、電話或 @username"
                className="w-full text-sm border rounded-md pl-8 pr-3 py-1.5 bg-background"
              />
            </div>
            {logsLoading ? (
              <div className="p-4 space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 rounded" />)}</div>
            ) : !logsData?.logs.length ? (
              <div className="p-6 text-center text-sm text-muted-foreground">暫無紀錄</div>
            ) : !filteredLogs.length ? (
              <div className="p-6 text-center text-sm text-muted-foreground">找不到符合的紀錄</div>
            ) : (
              <div className="divide-y max-h-96 overflow-y-auto">
                {filteredLogs.map(log => {
                  const isRebindConfirm = log.action === "confirmed" && log.reason === "rebind";
                  const meta = log.action === "registered"
                    ? { label: "登記", icon: LogIn, cls: "text-blue-600 bg-blue-50" }
                    : log.action === "confirmed"
                      ? isRebindConfirm
                        ? { label: "重新綁定", icon: RefreshCw, cls: "text-blue-600 bg-blue-50" }
                        : { label: "確認加入", icon: CheckCircle2, cls: "text-green-600 bg-green-50" }
                      : {
                          label: log.reason === "expired" ? "移除（會籍到期）" : log.reason === "rebind" ? "移除（重新綁定）" : "移除／重設",
                          icon: UserX, cls: "text-red-600 bg-red-50",
                        };
                  const Icon = meta.icon;
                  const newContact = log.username ? `@${log.username}` : log.phone;
                  return (
                    <div key={log.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium shrink-0 ${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      <div className="flex-1 min-w-0 truncate">
                        <span className="font-medium">{log.displayName ?? log.email}</span>
                        <span className="text-muted-foreground"> · {log.email}</span>
                        {isRebindConfirm ? (
                          <span className="text-muted-foreground">
                            {" "}· 刪除 <span className="text-red-600">{log.previousContact}</span>，加入 <span className="text-green-600">{newContact}</span>
                          </span>
                        ) : (
                          <>
                            {log.username && <span className="text-muted-foreground"> · @{log.username}</span>}
                            {log.phone && <span className="text-muted-foreground"> · {log.phone}</span>}
                          </>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{formatJoinedAt(log.createdAt)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜尋電郵、姓名或 WhatsApp 電話號碼…"
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-green-500/30"
          />
        </div>

        {!isLoading && duplicatePhoneGroups.length > 0 && (
          <div className="border border-red-200 bg-red-50/60 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-red-200 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-700" />
              <h3 className="text-sm font-semibold text-red-800">
                同一電話號碼被多位會員登記（{duplicatePhoneGroups.length}）
              </h3>
            </div>
            <div className="divide-y divide-red-200/60">
              {duplicatePhoneGroups.map(([phone, list]) => (
                <div key={phone} className="px-4 py-2.5 text-sm">
                  <div className="flex items-center gap-1.5 font-medium text-red-700 mb-1">
                    <Phone className="h-3.5 w-3.5" /> {phone}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground">
                    {list.map(m => (
                      <span key={m.id}>
                        {m.displayName ?? m.email} · {m.email}
                        {m.whatsappRequestStatus === "confirmed" ? "（已加入）" : m.whatsappRequestStatus === "pending" ? "（待審核）" : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && expiredButConfirmed.length > 0 && (
          <div className="border border-orange-200 bg-orange-50/60 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-orange-200 flex items-center gap-2">
              <UserX className="h-4 w-4 text-orange-700" />
              <h3 className="text-sm font-semibold text-orange-800">
                會籍已非付費，需從 WhatsApp 社群移除（{expiredButConfirmed.length}）
              </h3>
            </div>
            <div className="divide-y divide-orange-200/60">
              {expiredButConfirmed.map(m => (
                <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium">{m.displayName ?? m.email}</span>
                    <span className="text-muted-foreground"> · {m.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground shrink-0">
                    {m.whatsappUsername
                      ? <><AtSign className="h-3.5 w-3.5 text-muted-foreground" /> {m.whatsappUsername}</>
                      : <><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {m.whatsappPhone}</>}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs shrink-0 gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-100"
                    disabled={whatsappLoadingId === m.id}
                    onClick={() => resetWhatsApp.mutate({ id: m.id, reason: "expired" })}
                  >
                    {whatsappLoadingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />}
                    已從群組移除
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && pendingWhatsApp.length > 0 && (
          <div className="border border-green-200 bg-green-50/60 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-green-200 flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-green-700" />
              <h3 className="text-sm font-semibold text-green-800">待審核 WhatsApp 社群登記（{pendingWhatsApp.length}）</h3>
            </div>
            <div className="divide-y divide-green-200/60">
              {pendingWhatsApp.map(m => (
                <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium">{m.displayName ?? m.email}</span>
                    <span className="text-muted-foreground"> · {m.email}</span>
                    {m.whatsappPendingRemoval && (
                      <div className="text-xs text-red-600 font-medium flex items-center gap-1 mt-0.5">
                        <AlertTriangle className="h-3 w-3" /> 重新綁定：請先移除舊聯絡方式 {m.whatsappPendingRemoval}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground shrink-0">
                    {m.whatsappUsername
                      ? <><AtSign className="h-3.5 w-3.5 text-muted-foreground" /> {m.whatsappUsername}</>
                      : <><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {m.whatsappPhone}</>}
                  </div>
                  <Button
                    size="sm"
                    className="text-xs shrink-0 gap-1.5 bg-green-600 hover:bg-green-700"
                    disabled={whatsappLoadingId === m.id}
                    onClick={() => confirmWhatsApp.mutate(m.id)}
                  >
                    {whatsappLoadingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    已加入群組
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs shrink-0 gap-1.5 text-muted-foreground cursor-pointer"
                    disabled={whatsappLoadingId === m.id}
                    onClick={() => resetWhatsApp.mutate({ id: m.id })}
                  >
                    <Undo2 className="h-3.5 w-3.5" /> 重設
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
        ) : (
          <div className="border rounded-lg divide-y bg-card">
            {filteredMembers.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm">
                {searchQuery ? "沒有符合搜尋條件的會員" : "暫無會員記錄"}
              </div>
            )}
            {filteredMembers.map(m => {
              const expiry = formatExpiry(m.subscriptionCurrentPeriodEnd);
              return (
                <div key={m.id} className="flex items-center gap-4 px-4 py-3">
                  <div className={`p-1.5 rounded-md shrink-0 ${m.membershipTier === "paid" ? "bg-amber-50 text-amber-500" : "bg-secondary text-muted-foreground"}`}>
                    {m.membershipTier === "paid" ? <Crown className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.email}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5">
                      {m.displayName && <span className="font-semibold text-foreground">{m.displayName}</span>}
                      {m.notificationEmail && <span className="text-blue-600">聯絡電郵：{m.notificationEmail}</span>}
                      {m.stripeCustomerId && <span className="text-purple-600">Stripe: {m.stripeCustomerId.slice(0, 14)}…</span>}
                      {expiry && (
                        <span className="text-red-600 font-medium">到期：{expiry}</span>
                      )}
                      <span className="text-orange-400">加入日期：{formatJoinedAt(m.createdAt)}</span>
                      {m.whatsappRequestStatus === "confirmed" && (
                        <span className="text-green-600 flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" /> WhatsApp 已加入（{whatsappContactLabel(m)}）
                        </span>
                      )}
                      {(() => {
                        const contact = m.whatsappUsername ? `@${m.whatsappUsername.toLowerCase()}` : m.whatsappPhone;
                        return contact && duplicatePhoneSet.has(contact.replace(/[\s-]/g, "")) && (
                          <span className="text-red-600 flex items-center gap-1 font-medium">
                            <AlertTriangle className="h-3 w-3" /> {m.whatsappUsername ? "帳號重複" : "電話號碼重複"}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <Badge
                    variant={m.membershipTier === "paid" ? "outline" : "secondary"}
                    className={m.membershipTier === "paid" ? "bg-amber-50 text-amber-600 border-amber-200" : ""}
                  >
                    {m.membershipTier === "paid" ? "付費會員" : "免費會員"}
                  </Badge>

                  {m.whatsappRequestStatus === "confirmed" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs shrink-0 gap-1.5 text-red-600 hover:text-red-700 cursor-pointer"
                      disabled={whatsappLoadingId === m.id}
                      onClick={() => resetWhatsApp.mutate({ id: m.id })}
                    >
                      <Undo2 className="h-3.5 w-3.5" /> 重設 WhatsApp
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs shrink-0 gap-1.5 text-violet-600 border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                    disabled={sendingDailyId === m.id}
                    onClick={() => setConfirmMember(m)}
                  >
                    {sendingDailyId === m.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Mail className="h-3.5 w-3.5" />}
                    {sendingDailyId === m.id ? "發送中…" : "寄今日更新"}
                  </Button>

                  {m.membershipTier === "paid" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs shrink-0"
                      disabled={loadingId === m.id}
                      onClick={() => setTier.mutate({ id: m.id, tier: "free" })}
                      data-testid={`btn-set-free-${m.id}`}
                    >
                      改為免費
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="text-xs shrink-0"
                      disabled={loadingId === m.id}
                      onClick={() => setTier.mutate({ id: m.id, tier: "paid" })}
                      data-testid={`btn-set-paid-${m.id}`}
                    >
                      改為付費
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmMember !== null}
        title="寄送今日更新電郵"
        description={`確定發送今日更新電郵給 ${confirmMember?.email}？`}
        confirmLabel="確定發送"
        onConfirm={() => { const m = confirmMember!; setConfirmMember(null); doSendDailyEmail(m); }}
        onCancel={() => setConfirmMember(null)}
      />
    </AdminLayout>
  );
}
